import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Target, Factory, DollarSign } from "lucide-react";
import { formatCurrency } from "@/lib/config";
import { ProgressSection } from './DashboardHelpers';

export function SectionKPIVentes({
  deptView, periodLabel, periodView, month, quarter, year,
  deptVentesYTD, deptObjCA, deptPctVentes, deptBacklogTotal, deptBacklogNb,
  deptPaiementsTotal, deptPaiementsNb, paiementsAttente,
  ventesSource, setShowCADetail,
  // ProgressSection data
  objectifs, ventesYtd, ventesYTD, productionMois, productionMensuelle, productionAnnuelle, productionRealiseeAnn, revenusData,
  DEPTS, MONTHS, MOIS_COURT,
  canDo = () => true, // Défaut: tout visible (rétro-compatibilité)
}) {
  // Helper: objectifs par période
  const getObjForPeriod = (type, deptKey) => {
    if (periodView === 'mensuel') {
      return objectifs.filter(o => o.type_objectif === type && o.departement === deptKey && o.mois === month).reduce((s, o) => s + o.valeur, 0);
    } else if (periodView === 'trimestre') {
      const qStart = (quarter - 1) * 3 + 1;
      const qEnd = quarter * 3;
      return objectifs.filter(o => o.type_objectif === type && o.departement === deptKey && o.mois >= qStart && o.mois <= qEnd).reduce((s, o) => s + o.valeur, 0);
    }
    return objectifs.filter(o => o.type_objectif === type && o.departement === deptKey).reduce((s, o) => s + o.valeur, 0);
  };
  const periodSuffix = periodView === 'mensuel' ? MONTHS[month] : periodView === 'trimestre' ? `T${quarter}` : String(year);
  const deptItems = deptView === 'global' ? DEPTS : DEPTS.filter(d => d.key === deptView);

  const getVentesForPeriod = (key) => {
    const mensuel = ventesYtd?.mensuel || [];
    if (periodView === 'mensuel') {
      const m = mensuel.find(v => v.mois === month);
      return m ? (m[key] || 0) : 0;
    } else if (periodView === 'trimestre') {
      const qStart = (quarter - 1) * 3 + 1;
      const qEnd = quarter * 3;
      return mensuel.filter(v => v.mois >= qStart && v.mois <= qEnd).reduce((s, v) => s + (v[key] || 0), 0);
    }
    return ventesYTD[key] || 0;
  };

  const getRevenusForPeriod = (key) => {
    if (periodView === 'mensuel') {
      const m = revenusData.find(r => r.mois === month);
      return m ? (m[`revenus_${key.toLowerCase()}`] || 0) : 0;
    } else if (periodView === 'trimestre') {
      const qStart = (quarter - 1) * 3 + 1;
      const qEnd = quarter * 3;
      return revenusData.filter(r => r.mois >= qStart && r.mois <= qEnd).reduce((s, r) => s + (r[`revenus_${key.toLowerCase()}`] || 0), 0);
    }
    return revenusData.reduce((s, r) => s + (r[`revenus_${key.toLowerCase()}`] || 0), 0);
  };

  return (
    <>
      <div>
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-emerald-600" /> {deptView === 'LMDS' ? 'Production' : 'Ventes'} — {deptView !== 'global' ? deptView + ' — ' : ''}{periodLabel}
          {ventesSource === 'quickbooks' && <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600 border-blue-200">Source: QuickBooks</Badge>}
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {canDo('dashboard_kpi_ca_signe') && (
            <Card className="border-emerald-200 bg-emerald-50/40 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setShowCADetail('ytd')}>
              <CardContent className="pt-4 pb-3 px-4">
                <p className="text-xs text-emerald-700 font-semibold uppercase tracking-wide">{deptView === 'LMDS' ? `Production Totale ${periodView === 'annuel' ? 'YTD' : periodLabel}` : `CA Total Signe ${periodView === 'annuel' ? 'YTD' : periodLabel}`}</p>
                <p className="text-xl sm:text-2xl font-bold text-emerald-800 mt-1">{formatCurrency(deptVentesYTD)}</p>
                <p className="text-xs text-gray-500 mt-1">{deptView === 'LMDS' ? 'Source: rapports installateurs' : 'Cliquer pour détails'}</p>
              </CardContent>
            </Card>
          )}
          {canDo('dashboard_kpi_objectif_signature') && (
            <Card className="border-blue-200 bg-blue-50/40">
              <CardContent className="pt-4 pb-3 px-4">
                <p className="text-xs text-blue-700 font-semibold uppercase tracking-wide">{deptView === 'LMDS' ? 'Objectif Production' : 'Objectif Signature'} {periodView === 'mensuel' ? MONTHS[month] : periodView === 'trimestre' ? `T${quarter}` : 'annuel'}</p>
                <p className="text-xl sm:text-2xl font-bold text-blue-800 mt-1">{deptObjCA > 0 ? formatCurrency(deptObjCA) : '—'}</p>
                {deptObjCA > 0 && <p className="text-xs text-gray-500 mt-1">{deptPctVentes.toFixed(1)}% atteint</p>}
                {deptObjCA === 0 && <p className="text-xs text-amber-600 mt-1">Configurer dans Cibles et seuils</p>}
              </CardContent>
            </Card>
          )}
          {canDo('dashboard_kpi_ca_securise') && (
            <Card className="border-purple-200 bg-purple-50/40 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setShowCADetail('backlog')}>
              <CardContent className="pt-4 pb-3 px-4">
                <p className="text-xs text-purple-700 font-semibold uppercase tracking-wide">CA Securise{deptView === 'LMDS' ? ' pour l\'entreprise' : ''} <span className="text-purple-400 font-normal">(total cumulatif)</span></p>
                <p className="text-xl sm:text-2xl font-bold text-purple-800 mt-1">{formatCurrency(deptBacklogTotal)}</p>
                <p className="text-xs text-gray-500 mt-1">{deptBacklogNb} contrat(s) signés — Cliquer pour voir par mois</p>
              </CardContent>
            </Card>
          )}
          {canDo('dashboard_kpi_paiements') && (
            <Card className="border-amber-200 bg-amber-50/40">
              <CardContent className="pt-4 pb-3 px-4">
                <p className="text-xs text-amber-700 font-semibold uppercase tracking-wide">Paiements en attente <span className="text-amber-400 font-normal">(actuel)</span></p>
                <p className="text-xl sm:text-2xl font-bold text-amber-800 mt-1">{formatCurrency(deptView === 'LMDS' ? (paiementsAttente?.total_lmds_ht || 0) : deptPaiementsTotal)}</p>
                {deptView === 'LMDS' 
                  ? <p className="text-xs text-purple-600 mt-1">Entreprise: {formatCurrency(paiementsAttente?.total_lmds_entreprise || 0)}</p>
                  : <p className="text-xs text-gray-500 mt-1">{deptPaiementsNb} facture(s)</p>
                }
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Progression ventes par département */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {deptView !== 'LMDS' && canDo('dashboard_section_ventes_obj') && (
          <ProgressSection
            title={deptView === 'global' ? `Ventes vs Objectif — ${periodSuffix}` : `Ventes vs Objectif ${deptView} — ${periodSuffix}`}
            icon={Target}
            color="text-emerald-600"
            items={deptItems.map(({ key, label }) => ({
              label,
              reel: getVentesForPeriod(key),
              objectif: getObjForPeriod("CA_mensuel", key),
            }))}
            tooltip="Objectifs configurables dans Cibles et seuils"
          />
        )}
        {/* ✅ CORRECTION: Masquer "Booking vs Objectif" pour LMDS (concerne uniquement PRQC) */}
        {deptView !== 'LMDS' && canDo('dashboard_section_booking') && (
        <ProgressSection
          title={`Booking vs Objectif — ${periodSuffix}`}
          icon={Factory}
          color="text-blue-600"
          items={deptItems.map(({ key, label }) => {
            let prodVal;
            if (periodView === 'mensuel' && productionMensuelle) {
              prodVal = productionMensuelle[key] || 0;
            } else if (periodView === 'trimestre' && Array.isArray(productionAnnuelle)) {
              const qS = (quarter - 1) * 3 + 1, qE = quarter * 3;
              prodVal = productionAnnuelle.filter(p => p.mois >= qS && p.mois <= qE).reduce((s, p) => s + (p[key] || 0), 0);
            } else {
              prodVal = productionMois?.[key] || 0;
            }
            return { label, reel: prodVal, objectif: getObjForPeriod("production_mensuel", key) };
          })}
          tooltip="Valeur des contrats cedules au calendrier pour la periode."
        />
        )}
        {canDo('dashboard_section_production_realisee') && (
          <ProgressSection
            title={`Production realisee vs Objectif — ${periodSuffix}`}
            icon={Factory}
            color="text-orange-600"
            items={deptItems.map(({ key, label }) => {
              let prodVal = 0;
              if (Array.isArray(productionRealiseeAnn)) {
                if (periodView === 'mensuel') {
                  const m = productionRealiseeAnn.find(p => p.mois === month);
                  prodVal = m ? (m[key] || 0) : 0;
                } else if (periodView === 'trimestre') {
                  const qS = (quarter - 1) * 3 + 1, qE = quarter * 3;
                  prodVal = productionRealiseeAnn.filter(p => p.mois >= qS && p.mois <= qE).reduce((s, p) => s + (p[key] || 0), 0);
                } else {
                  prodVal = productionRealiseeAnn.reduce((s, p) => s + (p[key] || 0), 0);
                }
              }
              return { label, reel: prodVal, objectif: getObjForPeriod("production_mensuel", key) };
            })}
            tooltip="Valeur des travaux termines, au prorata des vrais jours de punch."
          />
        )}
        {canDo('dashboard_section_revenus') && (
          <ProgressSection
            title={`Revenus vs Objectif — ${periodSuffix}`}
            icon={DollarSign}
            color="text-purple-600"
            items={deptItems.map(({ key, label }) => ({
              label,
              reel: getRevenusForPeriod(key),
              objectif: getObjForPeriod("revenu_mensuel", key) || getObjForPeriod("CA_mensuel", key),
            }))}
            tooltip="Revenus = argent encaisse (QuickBooks). Objectif configurable dans Cibles et seuils > Objectifs Revenus."
          />
        )}
      </div>
    </>
  );
}
